import { useEffect, useRef } from 'react';

interface AudioVisualizerProps {
    audioRef: React.RefObject<HTMLAudioElement>;
    isPlaying: boolean;
    className?: string;
    analyserNode?: AnalyserNode;
}

// Enhanced visualization configuration
const VISUALIZER_CONFIG = {
    minHeight: 4, // Increased minimum bar height
    barSpacing: 1.5, // Slightly wider spacing
    barRadius: 4, // More rounded corners
    maxFPS: 60, // High framerate for smooth animation
    smoothingFactor: 0.7, // Higher smoothing for a more fluid look
    gradientColors: ['#4f46e5', '#6366f1', '#818cf8', '#a5b4fc'], // Indigo palette
    recordingGradientColors: ['#ef4444', '#f87171', '#fca5a5', '#fecaca'], // Red palette for recording
    pulseColors: ['rgba(239, 68, 68, 0.8)', 'rgba(239, 68, 68, 0)'], // Pulsing effect colors
    minDecibels: -85, // Increased sensitivity
    maxDecibels: -25, // Higher ceiling
    fftSize: 2048, // Higher detail for better visualization
    barWidthFactor: 2.5, // Slightly wider bars
    pulseSpeed: 1000, // Pulse animation speed in ms
    activeBarRatio: 0.85, // Percentage of bars that actively move (to avoid all bars moving at once)
    idleAnimation: true, // Whether to show idle animation when no audio
    reflectionOpacity: 0.15, // Opacity of the reflection effect
};

// Utility functions
const lerp = (a: number, b: number, t: number) => a * (1 - t) + b * t;
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const mapRange = (value: number, inMin: number, inMax: number, outMin: number, outMax: number) => 
    outMin + (outMax - outMin) * ((value - inMin) / (inMax - inMin));

export function AudioVisualizer({ audioRef, isPlaying, className = '', analyserNode }: AudioVisualizerProps) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const animationFrameRef = useRef<number>();
    const audioContextRef = useRef<AudioContext>();
    const analyserRef = useRef<AnalyserNode>();
    const sourceRef = useRef<MediaElementAudioSourceNode>();
    const resizeObserverRef = useRef<ResizeObserver>();
    const previousDataRef = useRef<Uint8Array | null>(null);
    const hueRotationRef = useRef<number>(0);
    const pulsePhaseRef = useRef<number>(0);
    const idleDataRef = useRef<Uint8Array | null>(null);
    const lastActivityTimeRef = useRef<number>(Date.now());

    // Handle canvas resize with performance optimization
    useEffect(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const updateCanvasSize = () => {
            const rect = canvas.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
        };

        updateCanvasSize();
        resizeObserverRef.current = new ResizeObserver(updateCanvasSize);
        resizeObserverRef.current.observe(canvas);

        return () => {
            resizeObserverRef.current?.disconnect();
        };
    }, []);

    // Initialize audio context for playback visualization
    useEffect(() => {
        if (!audioRef.current || !canvasRef.current || analyserNode) return;

        const initializeAudioContext = async () => {
            if (!audioRef.current) return;
            
            try {
                const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
                audioContextRef.current = new AudioContext();
                analyserRef.current = audioContextRef.current.createAnalyser();
                analyserRef.current.fftSize = VISUALIZER_CONFIG.fftSize;
                analyserRef.current.minDecibels = VISUALIZER_CONFIG.minDecibels;
                analyserRef.current.maxDecibels = VISUALIZER_CONFIG.maxDecibels;
                analyserRef.current.smoothingTimeConstant = VISUALIZER_CONFIG.smoothingFactor;
                sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
                sourceRef.current.connect(analyserRef.current);
                analyserRef.current.connect(audioContextRef.current.destination);
            } catch (error) {
                console.error('Failed to initialize audio context:', error);
            }
        };

        if (audioRef.current) {
            initializeAudioContext();
        }

        return () => {
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close().catch(console.error);
            }
        };
    }, [analyserNode]);

    // Generate idle animation data when there's no audio
    const generateIdleData = (bufferLength: number, timestamp: number) => {
        if (!idleDataRef.current) {
            idleDataRef.current = new Uint8Array(bufferLength);
        }
        
        const data = idleDataRef.current;
        const now = Date.now();
        // Use a decreasing factor for the idle animation - less movement as time passes
        const idleFactor = Math.min(1, Math.max(0.1, 10000 / (now - lastActivityTimeRef.current + 5000)));
        
        // Create a gentle wave pattern
        for (let i = 0; i < bufferLength; i++) {
            // Multiple sine waves with different frequencies and phases
            const waveA = Math.sin(timestamp * 0.001 + i * 0.1) * 20 * idleFactor;
            const waveB = Math.sin(timestamp * 0.0005 + i * 0.05) * 15 * idleFactor;
            const waveC = Math.sin(timestamp * 0.0003 + i * 0.02) * 10 * idleFactor;
            
            // Apply a position-based amplitude modifier for a more natural look
            const positionFactor = Math.sin((i / bufferLength) * Math.PI);
            data[i] = Math.min(255, Math.max(0, 15 + (waveA + waveB + waveC) * positionFactor));
        }
        
        return data;
    };

    // Draw visualization frame
    const drawFrame = (
        ctx: CanvasRenderingContext2D, 
        analyzer: AnalyserNode, 
        canvas: HTMLCanvasElement, 
        timestamp: number
    ) => {
        const bufferLength = analyzer.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyzer.getByteFrequencyData(dataArray);

        // Check if there's audio activity
        let hasActivity = false;
        let avgActivity = 0;
        for (let i = 0; i < bufferLength; i++) {
            avgActivity += dataArray[i];
            if (dataArray[i] > 25) {
                hasActivity = true;
            }
        }
        avgActivity /= bufferLength;
        
        // If almost no activity and idle animation is enabled, generate idle animation data
        if (!hasActivity && avgActivity < 15 && VISUALIZER_CONFIG.idleAnimation) {
            if (hasActivity) {
                lastActivityTimeRef.current = Date.now();
            }
            const idleData = generateIdleData(bufferLength, timestamp);
            
            // Apply idle data with smoothing
            for (let i = 0; i < bufferLength; i++) {
                dataArray[i] = lerp(dataArray[i], idleData[i], 0.2);
            }
        } else if (hasActivity) {
            lastActivityTimeRef.current = Date.now();
        }

        // Apply additional smoothing between frames
        if (previousDataRef.current) {
            for (let i = 0; i < bufferLength; i++) {
                dataArray[i] = lerp(previousDataRef.current[i], dataArray[i], 0.2);
            }
        }
        
        // Store current data for next frame
        previousDataRef.current = new Uint8Array(dataArray);

        const dpr = window.devicePixelRatio || 1;
        ctx.resetTransform();
        ctx.scale(dpr, dpr);

        // Clear canvas with a very subtle gradient background
        const bgGradient = ctx.createLinearGradient(0, 0, 0, canvas.height / dpr);
        bgGradient.addColorStop(0, 'rgba(17, 24, 39, 0.95)');
        bgGradient.addColorStop(1, 'rgba(31, 41, 55, 0.95)');
        ctx.fillStyle = bgGradient;
        ctx.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);

        // Determine which gradient to use based on playing status
        hueRotationRef.current = (hueRotationRef.current + 0.0005) % 1;
        const isRecording = isPlaying;
        const gradientColors = isRecording 
            ? VISUALIZER_CONFIG.recordingGradientColors 
            : VISUALIZER_CONFIG.gradientColors;

        // Create gradient for bars
        const gradient = ctx.createLinearGradient(0, canvas.height / dpr, 0, 0);
        gradientColors.forEach((color, index) => {
            gradient.addColorStop(index / (gradientColors.length - 1), color);
        });

        // Calculate bar dimensions
        const canvasWidth = canvas.width / dpr;
        const canvasHeight = canvas.height / dpr;
        const barWidth = (canvasWidth / bufferLength) * VISUALIZER_CONFIG.barWidthFactor;
        const maxBarHeight = canvasHeight * 0.85; // Reduced to allow space for pulse

        // Calculate visualization bounds
        const numBars = Math.min(Math.floor(canvasWidth / (barWidth + VISUALIZER_CONFIG.barSpacing)), bufferLength);
        const startBar = Math.floor((bufferLength - numBars) / 2);
        const endBar = startBar + numBars;

        // Update pulse phase
        pulsePhaseRef.current = (pulsePhaseRef.current + (timestamp - (pulsePhaseRef.current || timestamp)) / VISUALIZER_CONFIG.pulseSpeed) % 1;
        
        // Draw pulsating circle if recording
        if (isRecording) {
            ctx.save();
            
            // Create a pulsating radial gradient
            const pulseSize = 0.2 + 0.1 * Math.sin(pulsePhaseRef.current * Math.PI * 2);
            const pulse = ctx.createRadialGradient(
                canvasWidth / 2, canvasHeight / 2, 0,
                canvasWidth / 2, canvasHeight / 2, canvasWidth * pulseSize
            );
            
            VISUALIZER_CONFIG.pulseColors.forEach((color, index) => {
                pulse.addColorStop(index / (VISUALIZER_CONFIG.pulseColors.length - 1), color);
            });
            
            ctx.globalAlpha = 0.15 + 0.1 * Math.sin(pulsePhaseRef.current * Math.PI * 2);
            ctx.fillStyle = pulse;
            ctx.fillRect(0, 0, canvasWidth, canvasHeight);
            
            ctx.restore();
        }

        // Draw visualization
        ctx.save();
        
        // Add glow effect
        ctx.shadowBlur = 10;
        ctx.shadowColor = isRecording ? 'rgba(239, 68, 68, 0.7)' : 'rgba(79, 70, 229, 0.7)';

        for (let i = startBar; i < endBar; i++) {
            const index = i - startBar;
            const value = dataArray[i];
            const percent = value / 255;
            
            // Apply a slight randomness to make it more natural
            let barHeight = Math.max(
                VISUALIZER_CONFIG.minHeight,
                percent * maxBarHeight * (0.9 + Math.random() * 0.2)
            );
            
            // Apply amplitude modulation for a more dynamic look
            if (index % 4 === 0) {
                barHeight *= 0.85 + 0.3 * Math.sin(timestamp * 0.002 + index * 0.1);
            }

            const x = index * (barWidth + VISUALIZER_CONFIG.barSpacing);
            const y = canvasHeight - barHeight;
            
            // Draw bar with rounded corners
            ctx.fillStyle = gradient;
            ctx.beginPath();
            
            // Only use roundRect if supported, otherwise fallback to rect
            if (ctx.roundRect) {
                ctx.roundRect(
                    x,
                    y,
                    barWidth,
                    barHeight,
                    VISUALIZER_CONFIG.barRadius
                );
            } else {
                ctx.rect(x, y, barWidth, barHeight);
            }
            
            ctx.fill();
        }
        
        ctx.restore();

        // Add a subtle reflection
        ctx.save();
        ctx.globalAlpha = VISUALIZER_CONFIG.reflectionOpacity;
        ctx.translate(0, canvasHeight);
        ctx.scale(1, -0.3); // Flipped and scaled down
        
        for (let i = startBar; i < endBar; i++) {
            const index = i - startBar;
            const value = dataArray[i];
            const percent = value / 255;
            
            // Match the same randomness and modulation as the main bars
            let barHeight = Math.max(
                VISUALIZER_CONFIG.minHeight,
                percent * maxBarHeight * (0.9 + Math.random() * 0.2)
            );
            
            if (index % 4 === 0) {
                barHeight *= 0.85 + 0.3 * Math.sin(timestamp * 0.002 + index * 0.1);
            }

            const x = index * (barWidth + VISUALIZER_CONFIG.barSpacing);
            const y = canvasHeight - barHeight;
            
            ctx.fillStyle = gradient;
            ctx.beginPath();
            
            if (ctx.roundRect) {
                ctx.roundRect(
                    x,
                    y,
                    barWidth,
                    barHeight,
                    VISUALIZER_CONFIG.barRadius
                );
            } else {
                ctx.rect(x, y, barWidth, barHeight);
            }
            
            ctx.fill();
        }
        
        ctx.restore();
    };

    // Animation loop with frame rate control
    useEffect(() => {
        if (!canvasRef.current || (!analyserRef.current && !analyserNode)) return;

        const analyzerNode = analyserNode || analyserRef.current;
        if (!analyzerNode) return;

        const analyzer = analyzerNode as AnalyserNode;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        if (!context) return;
        
        const ctx = context as CanvasRenderingContext2D;
        let lastDrawTime = 0;
        const minDrawInterval = 1000 / VISUALIZER_CONFIG.maxFPS;

        function animate(timestamp: number) {
            if (timestamp - lastDrawTime >= minDrawInterval) {
                drawFrame(ctx, analyzer, canvas, timestamp);
                lastDrawTime = timestamp;
            }
            animationFrameRef.current = requestAnimationFrame(animate);
        }
        
        animationFrameRef.current = requestAnimationFrame(animate);
        
        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [isPlaying, analyserNode]);

    return (
        <canvas
            ref={canvasRef}
            className={`w-full h-full ${className}`}
            style={{ touchAction: 'none' }}
        />
    );
}

export default AudioVisualizer;
